import { OnInit } from '@angular/core';
/**
 * Angular core imports
 */
import { Component, Input, OnDestroy } from '@angular/core';

/**
 * service imports
 */
import { FunCollectionService } from '../../common/common-functions/fun-collection.service';
import { DataStorageService } from '../../dataservices/data-storage.service';
import { NewLanguageService } from '../../dataservices/new-language/new-language.service';

/**
 * model imports
 */
import { CommunityConfirmation, ConfirmationModalData, GrCommunity } from '../../onboarding/interface/global.model';

/**
 * constant imports
 */
import { ACTION_ON, ACTION_TYPE, COMMUNITY_JOIN_TYPE, COMMUNITY_PRIVACY, CTA_TYPE, ERROR_CODES, NUMBERS, PRODUCT_TYPE_CONSTANT, PRODUCT_TYPE_CONSTANT_NAME, SCREEN_NAME } from '../../constants/app.constants';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/**
 * sdk imports
 */
import { Analytics } from 'analytics2.0';
/**
 * Single community card on GR
 */
@Component({
  selector: 'app-single-community-card',
  templateUrl: './single-community-card.component.html',
  styleUrls: ['./single-community-card.component.scss']
})
export class SingleCommunityCardComponent implements OnInit, OnDestroy {

  @Input() community: GrCommunity;

  /**
   * @numbers Number constant
   * @withdrawRequest for community model
   * @withdrawRequestFlag flag for withdraw status
   * @confirmationModal flag for confirmation
   * @confirmationText confirmation modal text
   * @loading api request status
   */
  public numbers = NUMBERS;
  public withdrawRequest: CommunityConfirmation;
  public withdrawRequestFlag = false;
  public confirmationModal = false;
  public confirmedCommunity: CommunityConfirmation;
  public confirmationText: ConfirmationModalData;
  public loading = false;

  /**
   * Analytics session time.
   */
  private sessiontime = { 'start_time': '', 'end_time': '' };

  /**
   * @notifier$ for closing data stream
   */
  private notifier$ = new Subject();

  /**
   * REquired dependency
   */
  constructor(
    private dss: DataStorageService,
    private fcs: FunCollectionService,
    public mls: NewLanguageService,
    private analyticSdk: Analytics
  ) { }

  /**
   * On Init implementation
   */
  ngOnInit(): void {

  }

  /**
   *
   * @returns community creator detail
   */
  createdByText(): string {
    const msg = this.mls.languageText.community.key9;
    if (this.community.created_by.custom_id === this.dss.user_profile_data.profile.custom_id) {
      return `${msg} ${this.mls.languageText.community.key51}`;
    }
    return `${msg} ${this.community.created_by.full_name}`;
  }

  /**
   * @returns community type
   */
  getCommunityType(): string {
    return (this.community && this.community.privacy === COMMUNITY_PRIVACY.PUBLIC) ? COMMUNITY_PRIVACY.PUBLIC : COMMUNITY_PRIVACY.PRIVATE;
  }

  /**
   * navigate to community detail
   */
  navigateToDetail(): void {
    this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY,this.community?.da_id,this.confirmedCommunity.callingFrom !== '' ? this.confirmedCommunity.callingFrom : SCREEN_NAME.GRAND_ROUND, CTA_TYPE.CARD, ACTION_TYPE.CLICK, ACTION_ON.COMMUNITY, null);
    this.fcs.navigateToCommunityDetailPage(this.community.id);
  }

  /**
   * @returns  card CTA text
   */
  get ctaText(): string {
    return (this.community?.join_type === COMMUNITY_JOIN_TYPE.APPROVAL
      ? (this.withdrawRequestFlag || +this.community?.request?.response === NUMBERS.TWO ? this.mls.languageText.community.key88 : this.mls.languageText.community.key103)
      : this.mls.languageText.community.key72);
  }

  /**
   * Join public community event handler
   * @param community to join
   */
  joinCommunity(): void {
    if (this.withdrawRequestFlag || +this.community?.request?.response === NUMBERS.TWO) {
      this.withdrawRequest = {
        openModal: this.withdrawRequestFlag || +this.community?.request?.response === NUMBERS.TWO,
        community: this.community,
        callingFrom: ''
      };
      this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY, this.community?.da_id,this.confirmedCommunity.callingFrom !== '' ? this.confirmedCommunity.callingFrom : SCREEN_NAME.GRAND_ROUND, CTA_TYPE.BUTTON,ACTION_TYPE.CLICK, ACTION_ON.WITHDRAW_REQUEST,null);
      this.withdrawConfirmation(this.withdrawRequest);
    } else {
      this.community?.join_type === COMMUNITY_JOIN_TYPE.APPROVAL
        ? this.requestToJoinHandler()
        : this.joinCommunityHandler();
    }
  }

  /**
   * Event handler for sending request to admin to join public community
   */
  requestToJoinHandler(): void {
    this.loading = true;
    this.fcs.joinPublicCommunity(this.community.id, NUMBERS.ONE.toString()).pipe(
      takeUntil(this.notifier$)
    ).subscribe(response => {
      this.loading = false;
      if (response.status === NUMBERS.ONE && response.code === NUMBERS.TWO_THOUSAND) {
        this.fcs.showSuccess(this.mls.languageText.community.key89, this.mls.languageText.toster.key2);
        this.withdrawRequestFlag = true;
        this.fcs.navigateToCommunityDetailPage(this.community.id);

      }
      if (response.error && response.error.code === ERROR_CODES.ALREADY_EXIST) {
        this.withdrawRequestFlag = false;
        this.fcs.showSuccess(response.error.msg, this.mls.languageText.toster.key2);
      }
    });
  }

  /**
   * Join public community eventhandler
   */
  joinCommunityHandler(): void {
    this.loading = true;
    this.fcs.joinPublicCommunity(this.community.id).pipe(
      takeUntil(this.notifier$)
    ).subscribe(response => {
      this.loading = false;
      if (response.status === NUMBERS.ONE) {
        this.fcs.showSuccess(this.mls.languageText.toster.key2, this.mls.languageText.toster.key2);
        this.fcs.navigateToCommunityDetailPage(this.community.id);
        this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY, this.community?.da_id,this.confirmedCommunity.callingFrom !== '' ? this.confirmedCommunity.callingFrom : SCREEN_NAME.GRAND_ROUND, CTA_TYPE.BUTTON,ACTION_TYPE.CLICK, ACTION_ON.JOIN_COMMUNITY,null);
      } else {
        this.fcs.errorHandeler(response);
      }
    }, (error) => {
      this.loading = false;
      this.fcs.errorHandeler(error);
    });
  }

  /**
   * Confirmation withdraw request.
   */
  withdrawConfirmation(communityConfirmation: CommunityConfirmation): void {
    if (!this.confirmationText) {
      this.setConfirmationText();
      this.requestWithdrawStatus();
    }
    this.confirmedCommunity = communityConfirmation;
    this.confirmationModal = communityConfirmation.openModal;
  }

  /**
   * sets confirmation message for withdraw request
   */
  setConfirmationText(): void {
    this.confirmationText = {
      bodyText: this.mls.languageText.community.key91,
      headingText: this.mls.languageText.community.key92,
      allowButtonText: this.mls.languageText.community.key93,
      cancelButtonText: this.mls.languageText.community.key94
    };
  }

  /**
   * Allow withdraw request.
   */
  allowAction(isAllow: boolean): void {
    if (isAllow) {
      this.resetConfirmModal();
      /**
       * Set analytics data for analytics event.
       */
        this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY, this.community?.da_id,this.confirmedCommunity.callingFrom !== '' ? this.confirmedCommunity.callingFrom : SCREEN_NAME.GRAND_ROUND, CTA_TYPE.BUTTON,ACTION_TYPE.CLICK, ACTION_ON.WITHDRAW_REQUEST_CONFIRM,null);
    }
  }

  /**
   * Reset confirm modal.
   */
  resetConfirmModal(): void {
    this.confirmationModal = false;
  }

  /**
   * Withdraw actions to get on card CTA click.
   */
  requestWithdrawStatus(): void {
    this.fcs.getWithdrawStatus.pipe(
      takeUntil(this.notifier$))
      .subscribe(communityModal => {
        this.loading = true;
        if (communityModal) {
          this.fcs.joinPublicCommunity(
            this.withdrawRequest.community.id,
            this.numbers.ZERO.toString()).pipe(
              takeUntil(this.notifier$)
            ).subscribe(response => {
              this.loading = false;
              if (response.status === this.numbers.ONE && response.code === this.numbers.TWO_THOUSAND) {
                this.withdrawRequest.community.request.response = this.numbers.ZERO;
                this.withdrawRequest.community.request.type = '';
                this.fcs.showSuccess(this.mls.languageText.community.key90, this.mls.languageText.toster.key2);
                this.withdrawRequestFlag = false;
              }
              if (response.error && response.error.code === ERROR_CODES.ALREADY_EXIST) {
                this.fcs.showSuccess(response.error.msg, this.mls.languageText.toster.key2);
              }
            },
              (error) => {
                this.loading = false;
                this.fcs.errorHandeler(error);
              });
        }
      });
  }

  /**
   * for readmore cta status
   */
  get isReadMoreEnable(): boolean {
    return this.description.length > NUMBERS.ONE_SEVENTY;
  }

  /**
   * for community description
   */
  get description(): string {
    if (this.community?.promotional_text) {
      return this.community?.promotional_text;
    }
    if (this.community?.description) {
      return `${this.mls.languageText.community.key154}: ${this.community?.description}`;
    }
  }

  /**
   * product Share
   */
  shareProduct(): void {
    const shareProduct = [{
      share_url: '',
      share_contentt: null,
      product_type: PRODUCT_TYPE_CONSTANT.COMMUNITY,
      product_type_id: this.community.da_id
    }];
    this.fcs.productShare(shareProduct,this.confirmedCommunity.callingFrom !== '' ? this.confirmedCommunity.callingFrom : SCREEN_NAME.GRAND_ROUND);
  }

  /**
   * cleaning subscription
   */
  ngOnDestroy(): void {
    this.notifier$.next();
    this.notifier$.complete();
  }
}
